import React, { useCallback, useMemo, useRef } from 'react'
import { wrapDisplayName } from 'recompose'
import { mapValues } from 'lodash'
import { useAuth } from 'components/auth'
import { useDispatch } from 'react-redux'

// Injects an Auth0 token into each action before dispatch. The returned
// wrappers are referentially stable so they're safe in `useEffect` deps.
function withApiAuth(actionCreatorMap) {
  return (Component) => {
    function WrappedComponent({ ...rest }) {
      const dispatch = useDispatch()
      const { getAccessTokenSilently } = useAuth()
      const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently)
      getAccessTokenSilentlyRef.current = getAccessTokenSilently

      const actionCreatorWithAuth = useCallback(
        async (actionCreator, ...args) => {
          const accessToken = await getAccessTokenSilentlyRef.current()
          return await dispatch(actionCreator(...args, accessToken))
        },
        [dispatch],
      )

      const mappedActionCreators = useMemo(
        () =>
          mapValues(
            actionCreatorMap,
            (actionValue) =>
              (...args) =>
                actionCreatorWithAuth(actionValue, ...args),
          ),
        [actionCreatorWithAuth],
      )

      return <Component {...{ ...mappedActionCreators, ...rest }} />
    }

    WrappedComponent.displayName = wrapDisplayName(Component, 'withApiAuth')

    return WrappedComponent
  }
}

export default withApiAuth
